/**
 * Local Storage Manager for Insights
 * 
 * Manages tracking of viewed insights locally in the browser.
 * This provides a fallback mechanism while the backend API is being implemented.
 */

const VIEWED_INSIGHTS_KEY = 'viewed_insights';
const STORAGE_VERSION = 1;

interface ViewedInsightsStore {
  version: number;
  viewedInsights: {
    [insightId: string]: {
      viewedAt: number; // timestamp
      dateKey: string;
    };
  };
}

/**
 * Get the viewed insights store from localStorage
 */
function getStore(): ViewedInsightsStore {
  if (typeof window === 'undefined') {
    return { version: STORAGE_VERSION, viewedInsights: {} };
  }

  try {
    const stored = localStorage.getItem(VIEWED_INSIGHTS_KEY);
    if (!stored) {
      return { version: STORAGE_VERSION, viewedInsights: {} };
    }

    const store: ViewedInsightsStore = JSON.parse(stored);
    
    // Check version compatibility
    if (store.version !== STORAGE_VERSION) {
      console.log('[Insights Storage] Version mismatch, resetting store');
      return { version: STORAGE_VERSION, viewedInsights: {} };
    }

    return store;
  } catch (error) {
    console.error('[Insights Storage] Error loading store:', error);
    return { version: STORAGE_VERSION, viewedInsights: {} };
  }
}

/**
 * Save the viewed insights store to localStorage
 */
function saveStore(store: ViewedInsightsStore): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(VIEWED_INSIGHTS_KEY, JSON.stringify(store));
  } catch (error) {
    console.error('[Insights Storage] Error saving store:', error);
  }
}

/**
 * Mark an insight as viewed
 * @param insightId - The insight ID to mark as viewed
 * @param dateKey - The date key (YYYY-MM-DD format)
 */
export function markInsightAsViewed(insightId: string, dateKey: string): void {
  const store = getStore();
  
  store.viewedInsights[insightId] = {
    viewedAt: Date.now(),
    dateKey,
  };

  saveStore(store);
  console.log('[Insights Storage] Marked insight as viewed:', insightId);
}

/**
 * Check if an insight has been viewed
 * @param insightId - The insight ID to check
 * @returns true if the insight has been viewed
 */
export function isInsightViewed(insightId: string): boolean {
  const store = getStore();
  return !!store.viewedInsights[insightId];
}

/**
 * Get all viewed insights
 * @returns Array of insight IDs that have been viewed
 */
export function getViewedInsights(): string[] {
  const store = getStore();
  return Object.keys(store.viewedInsights);
}

/**
 * Get viewed timestamp for an insight
 * @param insightId - The insight ID
 * @returns Timestamp when the insight was viewed, or null if not viewed
 */
export function getViewedTimestamp(insightId: string): number | null {
  const store = getStore();
  return store.viewedInsights[insightId]?.viewedAt || null;
}

/**
 * Clear viewed insights older than specified days
 * @param daysToKeep - Number of days to keep (default: 30)
 */
export function cleanupOldViewedInsights(daysToKeep: number = 30): void {
  const store = getStore();
  const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
  
  let cleanedCount = 0;
  for (const [insightId, data] of Object.entries(store.viewedInsights)) {
    if (data.viewedAt < cutoffTime) {
      delete store.viewedInsights[insightId];
      cleanedCount++;
    }
  }

  if (cleanedCount > 0) {
    saveStore(store);
    console.log(`[Insights Storage] Cleaned up ${cleanedCount} old viewed insights`);
  }
}

/**
 * Clear all viewed insights
 */
export function clearAllViewedInsights(): void {
  const store = { version: STORAGE_VERSION, viewedInsights: {} };
  saveStore(store);
  console.log('[Insights Storage] Cleared all viewed insights');
}

/**
 * Get statistics about viewed insights
 */
export function getViewedInsightsStats(): {
  total: number;
  oldest: number | null;
  newest: number | null;
} {
  const store = getStore();
  const viewedList = Object.values(store.viewedInsights);
  
  if (viewedList.length === 0) {
    return { total: 0, oldest: null, newest: null };
  }

  const timestamps = viewedList.map(v => v.viewedAt);
  
  return {
    total: viewedList.length,
    oldest: Math.min(...timestamps),
    newest: Math.max(...timestamps),
  };
}
